import { z } from 'zod'

/**
 * 홈 배너 (BIZ-111). 운영자가 admin에서 관리하고, 홈 상단에 활성·기간 내 배너만 노출한다.
 * MVP는 전역 공통(개인화 없음). 목적지 link_url은 내부 경로(/…) 또는 http(s) URL.
 */

const dateOrNull = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD 형식의 날짜를 입력하세요')
    .nullable(),
)

/**
 * 운영자 관리 목록/폼 공용 배너 한 건.
 * imageUrl은 표시용으로 해석된 값(업로드면 서명 URL, 외부면 그 URL 그대로).
 * imageKey는 업로드로 저장된 키(있으면). 수정 시 서명 URL을 되쏘지 않고 키로 왕복한다.
 */
export const bannerResponse = z.object({
  id: z.number().int(),
  title: z.string(),
  imageUrl: z.string(),
  imageKey: z.string().nullable(),
  linkUrl: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  startsOn: z.string().nullable(),
  endsOn: z.string().nullable(),
})
export type BannerResponse = z.infer<typeof bannerResponse>

export const bannerListResponse = z.object({ items: z.array(bannerResponse) })
export type BannerListResponse = z.infer<typeof bannerListResponse>

/** 홈(공개) 노출용 최소 배너. 관리용 메타(활성·기간)는 빼고 그릴 것만. */
export const publicBannerResponse = z.object({
  id: z.number().int(),
  title: z.string(),
  imageUrl: z.string(),
  linkUrl: z.string().nullable(),
})
export type PublicBannerResponse = z.infer<typeof publicBannerResponse>

export const publicBannerListResponse = z.object({ items: z.array(publicBannerResponse) })
export type PublicBannerListResponse = z.infer<typeof publicBannerListResponse>

/**
 * 운영자 배너 등록·수정. 이미지는 **업로드 키(imageKey)** 를 기본으로 하되,
 * 외부 URL(imageUrl, 시드·레거시)도 허용한다 — 둘 중 하나는 반드시 있어야 한다.
 * link_url은 내부 경로(/…) 또는 http(s) URL만 허용.
 */
export const saveBannerRequest = z
  .object({
    title: z.string().trim().min(1).max(100),
    imageKey: z.string().min(1).max(512).optional(),
    imageUrl: z.string().url('이미지 URL을 입력하세요').max(2048).optional(),
    linkUrl: z
      .preprocess(
        (v) => (typeof v === 'string' && v.trim() === '' ? null : v),
        z
          .string()
          .max(500)
          .refine((s) => s.startsWith('/') || /^https?:\/\//.test(s), '내부 경로(/…) 또는 http(s) URL이어야 합니다')
          .nullable(),
      )
      .optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
    startsOn: dateOrNull.optional(),
    endsOn: dateOrNull.optional(),
  })
  .refine((d) => Boolean(d.imageKey) || Boolean(d.imageUrl), {
    message: '배너 이미지를 업로드하세요',
    path: ['imageKey'],
  })
export type SaveBannerRequest = z.infer<typeof saveBannerRequest>
